// src/pages/Hub/OpsHub.js
//
// Operations Hub — "Mission Control". One screen for company-wide health: a 3D globe
// of every region's jobs, a universal ⌘K finder, an animated health rail, a live
// activity feed, and a reports deck. Hidden, URL-only, public (per product choice).
//
// Data: /api/ops-hub/{overview,jobs,map,reports,search} which fan out to the region
// providers and merge in app code (Mideast isolation preserved server-side).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Navigation, AlertTriangle } from 'lucide-react';
import { fetchOverview, fetchJobs, fetchMap, fetchReports } from './lib/opsHubApi';
import { HUB_CSS, RANGE_PRESETS, rangeFor, regionMeta, C } from './theme';
import Globe from './components/Globe';
import CommandPalette from './components/CommandPalette';
import HealthRail from './components/HealthRail';
import PulseFeed from './components/PulseFeed';
import ReportsDeck from './components/ReportsDeck';
import DetailCanvas from './components/DetailCanvas';

export default function OpsHub() {
  const [rangeKey, setRangeKey] = useState('mtd');
  const range = useMemo(() => rangeFor(rangeKey), [rangeKey]);

  const [overview, setOverview] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [points, setPoints] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState({ overview: true, jobs: true, map: true });

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [hiddenRegions, setHiddenRegions] = useState(() => new Set());
  const [now, setNow] = useState(new Date());

  // live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ⌘K / Ctrl-K to open the finder
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // load everything on range change
  useEffect(() => {
    let cancelled = false;
    setLoading({ overview: true, jobs: true, map: true });
    fetchOverview(range).then((d) => !cancelled && setOverview(d)).catch(() => {}).finally(() => !cancelled && setLoading((l) => ({ ...l, overview: false })));
    fetchJobs(range).then((d) => !cancelled && setJobs(d.jobs || [])).catch(() => {}).finally(() => !cancelled && setLoading((l) => ({ ...l, jobs: false })));
    fetchMap(range).then((d) => !cancelled && setPoints(d.points || [])).catch(() => {}).finally(() => !cancelled && setLoading((l) => ({ ...l, map: false })));
    fetchReports(range).then((d) => !cancelled && setReports(d)).catch(() => {});
    return () => { cancelled = true; };
  }, [range]);

  const geoRegions = useMemo(() => Array.from(new Set(points.map((p) => p.region))), [points]);
  const visiblePoints = useMemo(() => points.filter((p) => !hiddenRegions.has(p.region)), [points, hiddenRegions]);

  const toggleRegion = (r) => setHiddenRegions((prev) => {
    const next = new Set(prev);
    if (next.has(r)) next.delete(r); else next.add(r);
    return next;
  });

  const selectClientResult = useCallback((r) => {
    setSelection({ kind: 'client', region: r.region, id: r.id, name: r.name, sub: r.sub, phone: r.phone, city: r.city, state: r.state });
    setPaletteOpen(false);
  }, []);

  const selectGlobePoint = useCallback((p) => {
    if (p.clientId) setSelection({ kind: 'client', region: p.region, id: p.clientId, name: p.clientName, sub: p.siteName });
    else setSelection({ kind: 'job', region: p.region, job: p, name: p.clientName, sub: p.siteName });
  }, []);

  const selectJob = useCallback((j) => {
    setSelection({ kind: 'job', region: j.region, job: j, name: j.clientName, sub: j.siteName });
  }, []);

  const selectReport = useCallback((key) => {
    setSelection({ kind: 'report', report: key });
  }, []);

  const errors = overview?.errors || [];

  return (
    <div className="hub-root">
      <style>{HUB_CSS}</style>

      {/* Top bar */}
      <motion.div className="hub-top" initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 220, damping: 22 }}>
        <div className="hub-brand">
          <span className="hub-brand-badge"><Navigation size={19} /></span>
          <div>
            <div className="hub-brand-name">Operations Hub</div>
            <div className="hub-brand-sub">Mission Control</div>
          </div>
        </div>

        <div className="hub-cmd" onClick={() => setPaletteOpen(true)}>
          <Search size={17} />
          <span className="hub-cmd-text">Search anything — client, contact, phone…</span>
          <span className="hub-cmd-kbd"><span>⌘</span><span>K</span></span>
        </div>

        <div className="hub-ranges">
          {RANGE_PRESETS.map((p) => (
            <button key={p.key} className={`hub-range ${rangeKey === p.key ? 'is-active' : ''}`} onClick={() => setRangeKey(p.key)}>
              {rangeKey === p.key && <motion.span layoutId="hubRangePill" className="hub-range-pill" transition={{ type: 'spring', stiffness: 360, damping: 30 }} />}
              <span className="hub-range-inner">{p.label}</span>
            </button>
          ))}
        </div>

        <div className="hub-clock">
          <div className="hub-clock-time">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          <div className="hub-clock-date"><span className="hub-live"><span className="hub-live-dot" /> LIVE</span> · {now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</div>
        </div>
      </motion.div>

      {/* Main: rail + globe */}
      <div className="hub-main">
        <HealthRail overview={overview} loading={loading.overview} />

        <div className="hub-globe-wrap">
          <div className="hub-globe-canvas">
            <Globe points={visiblePoints} onSelectPoint={selectGlobePoint} />
          </div>
          <div className="hub-globe-overlay">
            <div className="hub-globe-counter">
              <Navigation size={13} /> {loading.map ? 'mapping…' : `${visiblePoints.length} on map`}
            </div>
            <div className="hub-globe-regions">
              {geoRegions.map((r) => {
                const meta = regionMeta(r);
                const off = hiddenRegions.has(r);
                return (
                  <button key={r} className={`hub-rchip ${off ? 'off' : ''}`} onClick={() => toggleRegion(r)}>
                    <span className="hub-rchip-dot" style={{ background: meta.color, color: meta.color }} /> {meta.label}
                  </button>
                );
              })}
            </div>
            <div className="hub-globe-hint">
              {errors.length > 0
                ? <span style={{ color: C.amber }}><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> {errors.map((e) => regionMeta(e.region).short).join(', ')} unavailable</span>
                : 'drag to orbit · scroll to zoom · click a pin'}
            </div>
          </div>
        </div>
      </div>

      {/* Lower: feed + reports */}
      <div className="hub-lower">
        <PulseFeed jobs={jobs} loading={loading.jobs} onSelectJob={selectJob} />
        <ReportsDeck reports={reports} onOpen={selectReport} />
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onSelect={selectClientResult} />
      <DetailCanvas selection={selection} reports={reports} range={range} onClose={() => setSelection(null)} />
    </div>
  );
}
